import React, { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';

/**
 * SearchableSelect - A customizable searchable select component with single/multi-select support
 *
 * @param {Object} props
 * @param {Array} props.options - Array of options {id, name, ...}
 * @param {string|number|Array} props.value - Selected value(s). String/number for single, array for multi
 * @param {Function} props.onChange - Callback when selection changes (value, selectedItem(s))
 * @param {Function} props.onSearch - Async function to search options (query) => Promise<Array>
 * @param {boolean} props.loadOnOpen - Call onSearch('') when the dropdown opens so options show before typing
 * @param {number} props.pageSize - Enables infinite scroll: onSearch(query, page) is called for the next page while the last page returned `pageSize` items
 * @param {Function} props.onCreate - Async function to create new option (name) => Promise<{id, name, ...}>
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.label - Label text
 * @param {boolean} props.required - Is field required
 * @param {boolean} props.multiple - Enable multi-select mode
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.error - Error message to display
 * @param {number} props.debounceMs - Debounce delay for search (default: 300)
 * @param {number} props.minSearchLength - Minimum characters before searching (default: 1)
 * @param {string} props.createText - Text for "Add new" option (default: "Add new: {query}")
 * @param {Function} props.getOptionLabel - Function to get label from option (default: option => option.name)
 * @param {Function} props.getOptionValue - Function to get value from option (default: option => option.id)
 */
const SearchableSelect = ({
   options: initialOptions = [],
   value,
   onChange,
   onSearch,
   onCreate,
   placeholder,
   label,
   required = false,
   multiple = false,
   searchable = true,
   loadOnOpen = false,
   pageSize,
   className = '',
   error,
   debounceMs = 300,
   minSearchLength = 1,
   createText = (query) => `Add new: "${query}"`,
   getOptionLabel = (option) => option?.name || '',
   getOptionValue = (option) => option?.id || '',
   ...props
}) => {
   const [searchQuery, setSearchQuery] = useState('');
   const [filteredOptions, setFilteredOptions] = useState([]);
   const [isOpen, setIsOpen] = useState(false);
   const [isSearching, setIsSearching] = useState(false);
   const [isCreating, setIsCreating] = useState(false);
   const [highlightedIndex, setHighlightedIndex] = useState(-1);
   const [showCreateOption, setShowCreateOption] = useState(false);
   const [page, setPage] = useState(1);
   const [hasMore, setHasMore] = useState(false);
   const [isLoadingMore, setIsLoadingMore] = useState(false);

   const containerRef = useRef(null);
   const inputRef = useRef(null);
   const dropdownRef = useRef(null);
   const debounceTimerRef = useRef(null);

   // Get selected options
   const getSelectedOptions = useCallback(() => {
      if (!value) return [];
      if (multiple) {
         if (!Array.isArray(value)) return [];
         return initialOptions.filter((opt) => value.includes(getOptionValue(opt)));
      } else {
         return initialOptions.filter((opt) => getOptionValue(opt) === value);
      }
   }, [value, multiple, initialOptions, getOptionValue]);

   const selectedOptions = getSelectedOptions();
   const effectivePlaceholder = placeholder ?? (searchable ? 'Search...' : 'Select...');

   // Use refs to store latest callbacks to avoid dependency issues
   const onSearchRef = useRef(onSearch);
   const onCreateRef = useRef(onCreate);
   const getOptionLabelRef = useRef(getOptionLabel);
   const initialOptionsRef = useRef(initialOptions);
   const pageSizeRef = useRef(pageSize);

   // Update refs when props change (without causing re-renders)
   useEffect(() => {
      onSearchRef.current = onSearch;
      onCreateRef.current = onCreate;
      getOptionLabelRef.current = getOptionLabel;
      initialOptionsRef.current = initialOptions;
      pageSizeRef.current = pageSize;
   }, [onSearch, onCreate, getOptionLabel, initialOptions, pageSize]);

   // Debounce search - triggers on query change, or when opened with loadOnOpen
   useEffect(() => {
      // Clear any existing timer
      if (debounceTimerRef.current) {
         clearTimeout(debounceTimerRef.current);
      }

      const openWithEmptyQuery = loadOnOpen && isOpen && searchQuery.trim().length === 0;

      // Too short to search (and nothing to load on open): show initial options
      if (searchQuery.length < minSearchLength && !openWithEmptyQuery) {
         setFilteredOptions(initialOptionsRef.current);
         setShowCreateOption(false);
         setHasMore(false);
         return;
      }

      // Set up debounced search
      debounceTimerRef.current = setTimeout(
         async () => {
            const query = searchQuery.trim();

            if (!onSearchRef.current || (query.length < minSearchLength && !openWithEmptyQuery)) {
               setFilteredOptions(initialOptionsRef.current);
               setShowCreateOption(query.length >= minSearchLength && onCreateRef.current);
               setHasMore(false);
               return;
            }

            setIsSearching(true);
            try {
               const results = await onSearchRef.current(query, 1);
               setFilteredOptions(results || []);
               setPage(1);
               setHasMore(
                  Boolean(pageSizeRef.current) && (results?.length || 0) >= pageSizeRef.current
               );
               // Show create option if no exact match found
               const exactMatch = results?.some(
                  (opt) => getOptionLabelRef.current(opt).toLowerCase() === query.toLowerCase()
               );
               setShowCreateOption(!exactMatch && onCreateRef.current && query.length > 0);
            } catch (error) {
               console.error('Search error:', error);
               setFilteredOptions([]);
               setHasMore(false);
               setShowCreateOption(onCreateRef.current && query.length > 0);
            } finally {
               setIsSearching(false);
            }
         },
         openWithEmptyQuery ? 0 : debounceMs
      );

      return () => {
         if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
         }
      };
   }, [searchQuery, debounceMs, minSearchLength, loadOnOpen, isOpen]);

   // Infinite scroll: fetch the next page and append it
   const loadMore = async () => {
      if (!pageSize || !hasMore || isLoadingMore || isSearching || !onSearchRef.current) return;
      const nextPage = page + 1;
      setIsLoadingMore(true);
      try {
         const results = await onSearchRef.current(searchQuery.trim(), nextPage);
         setFilteredOptions((prev) => {
            const seen = new Set(prev.map((o) => getOptionValue(o)));
            return [...prev, ...(results || []).filter((o) => !seen.has(getOptionValue(o)))];
         });
         setPage(nextPage);
         setHasMore((results?.length || 0) >= pageSize);
      } catch (error) {
         console.error('Load more error:', error);
         setHasMore(false);
      } finally {
         setIsLoadingMore(false);
      }
   };

   const handleDropdownScroll = (e) => {
      const el = e.currentTarget;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) loadMore();
   };

   // Load initial options
   useEffect(() => {
      if (!onSearch && initialOptions.length > 0) {
         setFilteredOptions(initialOptions);
      } else if (!onSearch) {
         setFilteredOptions([]);
      }
   }, [initialOptions, onSearch]);

   // Handle click outside
   useEffect(() => {
      const handleClickOutside = (event) => {
         if (containerRef.current && !containerRef.current.contains(event.target)) {
            setIsOpen(false);
            setSearchQuery('');
            setHighlightedIndex(-1);
         }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
   }, []);

   // Handle keyboard navigation
   useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (e) => {
         const totalOptions = filteredOptions.length + (showCreateOption ? 1 : 0);

         switch (e.key) {
            case 'ArrowDown':
               e.preventDefault();
               setHighlightedIndex((prev) => (prev < totalOptions - 1 ? prev + 1 : prev));
               break;
            case 'ArrowUp':
               e.preventDefault();
               setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
               break;
            case 'Enter':
               e.preventDefault();
               if (highlightedIndex >= 0) {
                  if (highlightedIndex < filteredOptions.length) {
                     handleSelectOption(filteredOptions[highlightedIndex]);
                  } else if (showCreateOption) {
                     handleCreateNew();
                  }
               }
               break;
            case 'Escape':
               setIsOpen(false);
               setSearchQuery('');
               setHighlightedIndex(-1);
               break;
            default:
               console.warn('Not able to detect any activity on the search select input');
         }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
   }, [isOpen, filteredOptions, showCreateOption, highlightedIndex]);

   const handleSelectOption = (option) => {
      const optionValue = getOptionValue(option);

      if (multiple) {
         const currentValues = Array.isArray(value) ? value : [];
         const isSelected = currentValues.includes(optionValue);
         const newValues = isSelected
            ? currentValues.filter((v) => v !== optionValue)
            : [...currentValues, optionValue];
         onChange(
            newValues,
            newValues
               .map((v) => initialOptions.find((o) => getOptionValue(o) === v))
               .filter(Boolean)
         );
         // Clear the typed text so the next search starts fresh
         setSearchQuery('');
         inputRef.current?.focus();
      } else {
         onChange(optionValue, option);
         setIsOpen(false);
         setSearchQuery('');
      }
      setHighlightedIndex(-1);
   };

   const handleCreateNew = async () => {
      if (!onCreate || !searchQuery.trim()) return;

      setIsCreating(true);
      try {
         const newOption = await onCreate(searchQuery.trim());
         if (newOption) {
            handleSelectOption(newOption);
            setSearchQuery('');
         }
      } catch (error) {
         console.error('Create error:', error);
         toast.error(error.response?.data?.message || 'Failed to create item');
      } finally {
         setIsCreating(false);
      }
   };

   const handleRemoveChip = (optionValue) => {
      if (!multiple) return;
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.filter((v) => v !== optionValue);
      onChange(
         newValues,
         newValues.map((v) => initialOptions.find((o) => getOptionValue(o) === v)).filter(Boolean)
      );
   };

   const handleInputFocus = () => {
      setIsOpen(true);
      // Show initial options when opening if no search query
      if (searchQuery.length === 0) {
         setFilteredOptions(initialOptions);
      }
   };

   const handleInputChange = (e) => {
      setSearchQuery(e.target.value);
      setIsOpen(true);
      setHighlightedIndex(-1);
   };

   return (
      <div className={`mb-4 ${className}`} ref={containerRef}>
         {label && (
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
               {label}
               {required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
            </label>
         )}

         <div className="relative">
            {/* Input and selected chips */}
            <div
               className={`flex flex-wrap gap-2 min-h-[42px] px-3 py-2 border rounded-lg bg-white dark:bg-dark-primary text-gray-900 dark:text-gray-100 ${
                  error
                     ? 'border-red-500 dark:border-red-400'
                     : 'border-gray-300 dark:border-gray-600'
               } focus-within:ring-2 focus-within:ring-blue-500 dark:focus-within:ring-dark-accent focus-within:border-transparent`}
               onClick={() => inputRef.current?.focus()}
            >
               {/* Selected chips (multi-select) */}
               {multiple &&
                  selectedOptions.map((option) => {
                     const optionValue = getOptionValue(option);
                     return (
                        <span
                           key={optionValue}
                           className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 dark:bg-dark-accent text-blue-800 dark:text-white rounded-md"
                        >
                           {getOptionLabel(option)}
                           <button
                              type="button"
                              onClick={(e) => {
                                 e.stopPropagation();
                                 handleRemoveChip(optionValue);
                              }}
                              className="hover:text-blue-600 dark:hover:text-gray-300 focus:outline-none"
                           >
                              ×
                           </button>
                        </span>
                     );
                  })}

               {/* Search input */}
               <input
                  ref={inputRef}
                  type="text"
                  value={
                     searchable
                        ? isOpen
                           ? searchQuery
                           : !multiple && selectedOptions.length > 0
                             ? getOptionLabel(selectedOptions[0])
                             : ''
                        : !multiple && selectedOptions.length > 0
                          ? getOptionLabel(selectedOptions[0])
                          : ''
                  }
                  onChange={searchable ? handleInputChange : undefined}
                  onFocus={(e) => {
                     if (searchable && !multiple && selectedOptions.length > 0) {
                        setSearchQuery('');
                     }
                     handleInputFocus();
                  }}
                  readOnly={!searchable}
                  placeholder={effectivePlaceholder}
                  className={`flex-1 min-w-0 bg-transparent border-0 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 ${!searchable ? 'cursor-pointer caret-transparent' : ''}`}
                  {...props}
               />

               {/* Loading indicator */}
               {isSearching && (
                  <div className="flex items-center">
                     <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
               )}

               {/* Dropdown arrow */}
               <button
                  type="button"
                  onClick={() => {
                     setIsOpen(!isOpen);
                     inputRef.current?.focus();
                  }}
                  className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
               >
                  <svg
                     className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                     fill="none"
                     stroke="currentColor"
                     viewBox="0 0 24 24"
                  >
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                     />
                  </svg>
               </button>
            </div>

            {/* Dropdown */}
            {isOpen && (
               <div
                  ref={dropdownRef}
                  onScroll={handleDropdownScroll}
                  className="absolute z-50 w-full mt-1 bg-white dark:bg-dark-secondary border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
               >
                  {isSearching && filteredOptions.length === 0 ? (
                     <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                        Searching...
                     </div>
                  ) : filteredOptions.length === 0 && !showCreateOption ? (
                     <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No options found
                     </div>
                  ) : (
                     <>
                        {/* Options list */}
                        {filteredOptions.map((option, index) => {
                           const optionValue = getOptionValue(option);
                           const isSelected = multiple
                              ? Array.isArray(value) && value.includes(optionValue)
                              : value === optionValue;
                           const isHighlighted = index === highlightedIndex;

                           return (
                              <div
                                 key={optionValue}
                                 onClick={() => handleSelectOption(option)}
                                 className={`px-4 py-2 cursor-pointer text-sm ${
                                    isHighlighted
                                       ? 'bg-blue-100 dark:bg-dark-accent text-blue-900 dark:text-white'
                                       : 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-dark-primary'
                                 } ${isSelected ? 'font-semibold' : ''}`}
                              >
                                 <div className="flex items-center gap-2">
                                    <span>{getOptionLabel(option)}</span>
                                    {isSelected && (
                                       <svg
                                          className="ml-auto w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                       >
                                          <path
                                             strokeLinecap="round"
                                             strokeLinejoin="round"
                                             strokeWidth={2.5}
                                             d="M5 13l4 4L19 7"
                                          />
                                       </svg>
                                    )}
                                 </div>
                              </div>
                           );
                        })}

                        {isLoadingMore && (
                           <div className="px-4 py-2 text-sm text-center text-gray-500 dark:text-gray-400">
                              Loading more...
                           </div>
                        )}

                        {/* Create new option */}
                        {showCreateOption && (
                           <div
                              onClick={handleCreateNew}
                              className={`px-4 py-2 cursor-pointer text-sm border-t border-gray-200 dark:border-gray-700 ${
                                 highlightedIndex === filteredOptions.length
                                    ? 'bg-blue-100 dark:bg-dark-accent text-blue-900 dark:text-white'
                                    : 'text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-dark-primary'
                              } ${isCreating ? 'opacity-50 cursor-wait' : ''}`}
                           >
                              <div className="flex items-center gap-2">
                                 <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                 >
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={2}
                                       d="M12 4v16m8-8H4"
                                    />
                                 </svg>
                                 <span>{isCreating ? 'Creating...' : createText(searchQuery)}</span>
                              </div>
                           </div>
                        )}
                     </>
                  )}
               </div>
            )}
         </div>

         {error && <p className="mt-1 text-sm text-red-500 dark:text-red-400">{error}</p>}
      </div>
   );
};

export default SearchableSelect;
